// src/pages/HmoLinks.jsx - HMO Insurance Links Management
// ✅ FIXED: AgentId included in copied link URLs for attribution tracking
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { authenticatedFetch, clearStoredCredentials } from '../../utils/api';

// PrimeReact + Chart.js
import { Chart } from 'primereact/chart';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { ProgressBar } from 'primereact/progressbar';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { FilterMatchMode, FilterOperator } from 'primereact/api';

// 🎨 Orange Theme Colors
const BRAND_ORANGE = '#F89423';
const BRAND_ORANGE_DARK = '#ea7c1f';
const BRAND_ORANGE_LIGHT = '#fef3e2';

const CHART_COLORS = {
  primary: BRAND_ORANGE,
  secondary: '#8b5cf6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
  gray: '#6b7280'
};

const API_BASE = process.env.REACT_APP_API_URL || 'https://localhost:7018';

// ✅ HELPER: Get session data (agentSession or userSession fallback)
const getHmoSession = () => {
  try {
    return JSON.parse(
      localStorage.getItem('agentSession') || 
      localStorage.getItem('userSession') || 
      '{}'
    );
  } catch {
    return {};
  }
};

// ✅ HELPER: Get numeric CompanyId from session
const getCompanyId = () => {
  try {
    const session = getHmoSession();
    
    if (session.companyId !== undefined && session.companyId !== null) {
      const parsed = parseInt(session.companyId);
      if (!isNaN(parsed) && parsed > 0) return parsed;
    }
    
    if (typeof session.companyId === 'string' && session.companyId.trim() !== '') {
      const parsed = parseInt(session.companyId);
      if (!isNaN(parsed) && parsed > 0) return parsed;
    }
    
    if (session.sessionId) {
      console.warn('⚠️ Session has sessionId but missing/invalid companyId');
    }
    
    return null;
  } catch (error) {
    console.error('❌ Error parsing session:', error);
    return null;
  }
};

// ✅ HELPER: Get agent info from session (for link attribution)
const getAgentInfo = () => {
  try {
    const session = getHmoSession();
    return {
      agentId: session.agentId || session.sessionId,  // Hex: "B6283D10F0B0"
      companyId: session.companyId,                    // INT: 46
      userEmail: session.userEmail
    };
  } catch {
    return { agentId: null, companyId: null, userEmail: null };
  }
};

// ✅ Generate Link URL - NOW INCLUDES AGENTID FOR ATTRIBUTION
const generateLinkUrl = (companyId, linkId) => {
  const { agentId } = getAgentInfo();
  
  const baseUrl = `${window.location.origin}/insurance/landing`;
  const params = new URLSearchParams({
    company: String(companyId),
    link: String(linkId)
  });
  
  // ✅ Append agentId if available (for attribution tracking)
  if (agentId) {
    params.append('agent', agentId);
  }
  
  return `${baseUrl}?${params.toString()}`;
};

function HmoLinks() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // UI State
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // API State
  const [insuranceLinks, setInsuranceLinks] = useState([]);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter & Search State
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    linkTitle: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    linkId: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    status: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    operations: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] }
  });
  const [globalFilterValue, setGlobalFilterValue] = useState('');
  
  // Modal State
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);
  const [activeDetailsTab, setActiveDetailsTab] = useState('details');
  const [copiedId, setCopiedId] = useState(null);
  
  // Form State
  const [generateForm, setGenerateForm] = useState({
    linkTitle: '',
    operations: [],
    productIds: [],
    purchaseCharge: 'Customer',
    renewalCharge: 'Customer',
    chargeMode: 'Disabled',
    expiryType: 'indefinite',
    customExpiry: ''
  });
  const [generateErrors, setGenerateErrors] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  
  const [editForm, setEditForm] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  
  const deleteRef = React.useRef(null);

  // ✅ Fetch Products from /Settings/products endpoint
  const fetchProducts = useCallback(async () => {
    try {
      setProductsLoading(true);
      const response = await authenticatedFetch(`${API_BASE}/Settings/products`, 'POST', {});
      
      const isSuccess = response?.success === true || response?.status === 'success';
      if (isSuccess) {
        const apiData = response.data || response.result || [];
        const formatted = apiData.map(p => ({
          id: p.id || p.Id,
          name: p.name || p.Name,
          category: p.categoryName || p.CategoryName,
          premium: p.minimumPremium ?? p.MinimumPremium
        }));
        setProducts(formatted);
      } else {
        throw new Error(response?.message || 'Failed to fetch products');
      }
    } catch (err) {
      console.error('💥 Products fetch error:', err);
      toast.error('Could not load products');
    } finally {
      setProductsLoading(false);
    }
  }, []);

  // ✅ Fetch Insurance Links
  const fetchInsuranceLinks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const companyId = getCompanyId();
      
      if (!companyId || isNaN(companyId) || companyId <= 0) {
        console.error('❌ Invalid companyId:', companyId);
        toast.error('Invalid session. Please log in again.');
        clearStoredCredentials();
        localStorage.removeItem('agentSession');
        localStorage.removeItem('userSession');
        navigate('/login', { replace: true });
        return;
      }
      
      const response = await authenticatedFetch(
        `${API_BASE}/Settings/links/company/${companyId}?userType=hmo`,
        'POST',
        {}
      );
      
      const isSuccess = response?.success === true || response?.status === 'success';
      if (isSuccess) {
        const apiData = response.data || response.result || [];
        
        const normalizedLinks = apiData.map(link => ({
          ...link,
          bundledProducts: (link.bundledProducts || []).map(p => ({
            id: p.id || p.Id,
            name: p.name || p.Name,
            categoryId: p.categoryId || p.CategoryId,
            categoryName: p.categoryName || p.CategoryName,
            minimumPremium: p.minimumPremium ?? p.MinimumPremium,
            status: p.status || p.Status
          }))
        }));
        
        console.log('✅ Links loaded:', normalizedLinks.length);
        setInsuranceLinks(normalizedLinks);
      } else {
        const msg = (response?.message || '').toLowerCase();
        if (msg.includes('expired') || msg.includes('unauthorized') || msg.includes('401') || msg.includes('conversion failed')) {
          clearStoredCredentials();
          localStorage.removeItem('agentSession');
          localStorage.removeItem('userSession');
          toast.warning('Session expired. Please log in again.');
          navigate('/login', { replace: true });
          return;
        }
        throw new Error(response?.message || 'Failed to fetch links');
      }
    } catch (err) {
      console.error('💥 Links fetch error:', err);
      const errorMsg = err.message?.toLowerCase() || '';
      
      if (errorMsg.includes('401') || errorMsg.includes('expired') || errorMsg.includes('conversion failed')) {
        clearStoredCredentials();
        localStorage.removeItem('agentSession');
        localStorage.removeItem('userSession');
        toast.warning('Session error. Please log in again.');
        navigate('/login', { replace: true });
      } else {
        setError(err.message || 'Could not load insurance links');
        toast.error(err.message || 'Could not load insurance links');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // ✅ Load data on mount
  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
    fetchProducts();
    fetchInsuranceLinks();
  }, [fetchProducts, fetchInsuranceLinks]);

  // ✅ Toggle Dropdown (for sidebar)
  const toggleDropdown = (path) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };

  const isExpanded = (path) => expandedItems.has(path);

  // ✅ Format Currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // ✅ Copy to Clipboard (with agent attribution already in URL)
  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      toast.success('Link copied with agent attribution!');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedId(id);
      toast.success('Link copied with agent attribution!');
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  // ✅ Modal Handlers
  const handleViewDetails = (link) => {
    setSelectedLink(link);
    setShowDetailsModal(true);
    setActiveDetailsTab('details');
  };
  
  const handleCloseModal = () => {
    setShowDetailsModal(false);
    setTimeout(() => setSelectedLink(null), 300);
  };
  
  const handleOpenGenerateModal = () => {
    setGenerateForm({
      linkTitle: '', operations: [], productIds: [],
      purchaseCharge: 'Customer', renewalCharge: 'Customer',
      chargeMode: 'Disabled', expiryType: 'indefinite', customExpiry: ''
    });
    setGenerateErrors({});
    setShowGenerateModal(true);
  };
  
  const handleOpenEditModal = (link) => {
    const ops = link.operations?.split(',').map(o => o.trim()).filter(Boolean) || [];
    const productIds = link.bundledProducts?.map(p => p.id) || [];
    
    setEditForm({
      id: link.id, linkTitle: link.linkTitle, operations: ops,
      productIds: productIds,
      purchaseCharge: link.purchaseCharge || 'Customer',
      renewalCharge: link.renewalCharge || 'Customer',
      chargeMode: link.chargeMode || 'Disabled',
      expiryType: link.expiryDate === 'Indefinite' ? 'indefinite' : 'custom',
      customExpiry: link.expiryDate !== 'Indefinite' ? link.expiryDate : '',
      status: link.status
    });
    setEditErrors({});
    setShowEditModal(true);
  };
  
  const handleCloseGenerateModal = () => { setShowGenerateModal(false); setGenerateErrors({}); };
  const handleCloseEditModal = () => { setShowEditModal(false); setEditErrors({}); };

  // ✅ Form Change Handlers
  const handleGenerateChange = (field, value) => {
    setGenerateForm(prev => ({ ...prev, [field]: value }));
    if (generateErrors[field]) setGenerateErrors(prev => ({ ...prev, [field]: '' }));
  };
  
  const handleGenerateToggle = (field, value) => {
    setGenerateForm(prev => {
      const current = prev[field] || [];
      return {
        ...prev,
        [field]: current.includes(value)
          ? current.filter(v => v !== value)
          : [...current, value]
      };
    });
  };
  
  const handleEditChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
    if (editErrors[field]) setEditErrors(prev => ({ ...prev, [field]: '' }));
  };
  
  const handleEditToggle = (field, value) => {
    setEditForm(prev => {
      const current = prev[field] || [];
      return {
        ...prev,
        [field]: current.includes(value)
          ? current.filter(v => v !== value)
          : [...current, value]
      };
    });
  };

  // ✅ Validation
  const validateGenerateForm = () => {
    const errors = {};
    if (!generateForm.linkTitle?.trim()) errors.linkTitle = 'Link title is required';
    if (!generateForm.operations?.length) errors.operations = 'Select at least one operation';
    if (!generateForm.productIds?.length) errors.productIds = 'Select at least one product';
    if (generateForm.expiryType === 'custom' && !generateForm.customExpiry)
      errors.customExpiry = 'Please select an expiry date';
    setGenerateErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const validateEditForm = () => {
    const errors = {};
    if (!editForm.linkTitle?.trim()) errors.linkTitle = 'Link title is required';
    if (!editForm.operations?.length) errors.operations = 'Select at least one operation';
    if (!editForm.productIds?.length) errors.productIds = 'Select at least one product';
    if (editForm.expiryType === 'custom' && !editForm.customExpiry)
      errors.customExpiry = 'Please select an expiry date';
    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ✅ API: Generate/Save Link
  const handleGenerateSubmit = async (e) => {
    e.preventDefault();
    if (!validateGenerateForm()) return;
    
    try {
      setIsGenerating(true);
      const companyId = getCompanyId();
      
      if (!companyId || isNaN(companyId) || companyId <= 0) {
        toast.error('Invalid session. Please log in again.');
        clearStoredCredentials();
        localStorage.removeItem('agentSession');
        localStorage.removeItem('userSession');
        navigate('/login', { replace: true });
        return;
      }
      
      const payload = {
        companyId,
        linkTitle: generateForm.linkTitle.trim(),
        operations: generateForm.operations,
        productIds: generateForm.productIds,
        purchaseCharge: generateForm.purchaseCharge,
        renewalCharge: generateForm.renewalCharge,
        chargeMode: generateForm.chargeMode,
        expiryDate: generateForm.expiryType === 'indefinite' ? null : generateForm.customExpiry,
        status: 'Active'
      };
      
      const response = await authenticatedFetch(
        `${API_BASE}/Settings/create-insurance-link`,
        'POST',
        payload
      );
      
      if (response?.success === true) {
        toast.success('Link created successfully!');
        handleCloseGenerateModal();
        await fetchInsuranceLinks();
      } else {
        throw new Error(response?.message || 'Failed to create link');
      }
    } catch (err) {
      console.error('💥 Create link error:', err);
      toast.error(err.message || 'Failed to create link');
    } finally {
      setIsGenerating(false);
    }
  };

  // ✅ API: Update Link
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!validateEditForm()) return;
    
    try {
      setIsEditing(true);
      const companyId = getCompanyId();
      
      if (!companyId || isNaN(companyId) || companyId <= 0) {
        toast.error('Invalid session. Please log in again.');
        clearStoredCredentials();
        localStorage.removeItem('agentSession');
        localStorage.removeItem('userSession');
        navigate('/login', { replace: true });
        return;
      }
      
      const payload = {
        id: editForm.id,
        companyId,
        linkTitle: editForm.linkTitle?.trim(),
        operations: editForm.operations,
        productIds: editForm.productIds,
        purchaseCharge: editForm.purchaseCharge,
        renewalCharge: editForm.renewalCharge,
        chargeMode: editForm.chargeMode,
        expiryDate: editForm.expiryType === 'indefinite' ? null : editForm.customExpiry,
        status: editForm.status
      };
      
      const response = await authenticatedFetch(
        `${API_BASE}/Settings/update-insurance-link`,
        'POST',
        payload
      );
      
      if (response?.success === true) {
        toast.success('Link updated successfully!');
        handleCloseEditModal();
        await fetchInsuranceLinks();
      } else {
        throw new Error(response?.message || 'Failed to update link');
      }
    } catch (err) {
      console.error('💥 Update link error:', err);
      toast.error(err.message || 'Failed to update link');
    } finally {
      setIsEditing(false);
    }
  };

  // ✅ API: Delete Link
  const handleDeleteLink = (event, linkId, linkTitle) => {
    confirmPopup({
      target: event.currentTarget,
      message: `Are you sure you want to delete "${linkTitle}"?`,
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          const response = await authenticatedFetch(
            `${API_BASE}/Settings/delete-insurance-link`,
            'POST',
            { id: linkId }
          );
          
          if (response?.success === true) {
            setInsuranceLinks(prev => prev.filter(l => l.id !== linkId));
            if (selectedLink?.id === linkId) handleCloseModal();
            toast.success('Link deleted successfully');
          } else {
            throw new Error(response?.message || 'Delete failed');
          }
        } catch (err) {
          console.error('💥 Delete error:', err);
          toast.error(err.message || 'Failed to delete link');
        }
      },
      reject: () => {
        toast.info('Delete cancelled');
      }
    });
  };

  // ✅ API: Toggle Status
  const handleToggleStatus = async (linkId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'Active' ? 'Inactive' : 'Active';
      const response = await authenticatedFetch(
        `${API_BASE}/Settings/update-insurance-link-status`,
        'POST',
        { id: linkId, status: newStatus }
      );
      
      if (response?.success === true) {
        setInsuranceLinks(prev => prev.map(l => 
          l.id === linkId ? { ...l, status: newStatus, linkStatus: newStatus } : l
        ));
        if (selectedLink?.id === linkId) {
          setSelectedLink(prev => prev ? { ...prev, status: newStatus, linkStatus: newStatus } : null);
        }
        toast.success(`Link ${newStatus.toLowerCase()}!`);
      } else {
        throw new Error(response?.message || 'Update failed');
      }
    } catch (err) {
      console.error('💥 Toggle status error:', err);
      toast.error(err.message || 'Failed to update status');
    }
  };

  // ✅ Handle Logout
  const handleLogout = (e) => {
    e?.preventDefault();
    clearStoredCredentials();
    localStorage.removeItem('agentSession');
    localStorage.removeItem('userSession');
    toast.success('Logged out successfully');
    navigate('/login', { replace: true });
  };

  // ✅ Advanced Filter Handlers
  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters['global'].value = value;
    setFilters(_filters);
    setGlobalFilterValue(value);
  };
  
  const clearFilter = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      linkTitle: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
      linkId: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
      status: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
      operations: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] }
    });
    setGlobalFilterValue('');
  };

  // ✅ Export Functions
  const exportCSV = () => {
    toast.info('Export feature coming soon!');
  };

  // ✅ Status Badge Template
  const statusBodyTemplate = (rowData) => {
    const getSeverity = (status) => {
      switch (status) {
        case 'Active': return 'success';
        case 'Inactive': return 'danger';
        default: return null;
      }
    };
    return (
      <Tag value={rowData.status || 'N/A'} severity={getSeverity(rowData.status)} className="fw-semibold" />
    );
  };

  // ✅ Status Filter Template
  const statusFilterTemplate = (options) => {
    return (
      <Dropdown
        value={options.value}
        options={[
          { label: 'Active', value: 'Active' },
          { label: 'Inactive', value: 'Inactive' }
        ]}
        onChange={(e) => options.filterCallback(e.value, options.index)}
        optionLabel="label"
        optionValue="value"
        placeholder="Select Status"
        className="p-column-filter"
        showClear
      />
    );
  };

  // ✅ Operations Badge Template
  const operationsBodyTemplate = (rowData) => (
    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
      {rowData.operations?.split(',').map((op, idx) => (
        <Tag key={idx} value={op.trim()} severity="info" style={{ fontSize: '11px', padding: '2px 8px' }} />
      ))}
    </div>
  );

  // ✅ Action Buttons Template
  const actionBodyTemplate = (rowData) => (
    <div className="flex gap-2">
      <Button
        icon="pi pi-pencil"
        className="p-button-rounded p-button-success p-button-sm"
        onClick={() => handleOpenEditModal(rowData)}
        tooltip="Edit"
        style={{ backgroundColor: BRAND_ORANGE, borderColor: BRAND_ORANGE }}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-rounded p-button-danger p-button-sm"
        onClick={(e) => handleDeleteLink(e, rowData.id, rowData.linkTitle)}
        tooltip="Delete"
        ref={deleteRef}
      />
      <Button
        icon="pi pi-eye"
        className="p-button-rounded p-button-info p-button-sm"
        onClick={() => handleViewDetails(rowData)}
        tooltip="View Details"
        style={{ backgroundColor: '#3b82f6', borderColor: '#3b82f6' }}
      />
    </div>
  );

  // ✅ Render Header
  const renderHeader = () => {
    return (
      <div className="flex justify-content-between align-items-center flex-wrap gap-3">
        <div className="flex align-items-center gap-2">
          <Button
            type="button"
            icon="pi pi-filter-slash"
            label="Clear Filters"
            outlined
            onClick={clearFilter}
            size="small"
            style={{
              borderRight: '1px solid #e5e7eb',
              paddingRight: '1rem',
              marginRight: '0.5rem',
              marginTop: '1rem',
              borderRadius: '6px 0 0 6px'
            }}
          />
          <IconField iconPosition="left">
            <InputIcon className="pi pi-search" />
            <InputText
              value={globalFilterValue}
              onChange={onGlobalFilterChange}
              placeholder="Keyword Search"
              style={{
                width: '250px',
                borderRadius: '0 6px 6px 0',
                borderLeft: 'none',
                marginTop: '1rem',
              }}
            />
          </IconField>
        </div>
        <div className="flex align-items-center gap-2">
          <Button
            type="button"
            icon="pi pi-file"
            rounded
            onClick={exportCSV}
            data-pr-tooltip="CSV"
            aria-label="Export CSV"
          />
          <Button
            type="button"
            icon="pi pi-plus"
            label="Generate Link"
            onClick={handleOpenGenerateModal}
            style={{ backgroundColor: BRAND_ORANGE, borderColor: BRAND_ORANGE }}
            size="small"
          />
        </div>
      </div>
    );
  };

  // ✅ Nav Items for HMO Sidebar
  const navItems = useMemo(() => {
    const currentPath = location.pathname;
    return [
      {
        section: 'MAIN',
        items: [
          { name: 'Dashboard', icon: 'fa-chart-line', path: '/hmo-dashboard', active: currentPath === '/hmo-dashboard' }
        ]
      },
      {
        section: 'Plan',
        items: [
          { name: 'Plan link', icon: 'fa-user', path: '/agent-links ', active: currentPath === 'agent-links ' }
         
        ]
      }
      
    ];
  }, [location.pathname]);

  // ✅ Stats
  const stats = useMemo(() => ({
    total: insuranceLinks.length,
    active: insuranceLinks.filter(l => l.status === 'Active').length,
    multiple: insuranceLinks.filter(l => l.linkType === 'Multiple').length,
    totalUsage: insuranceLinks.reduce((sum, l) => sum + (l.usageCount || 0), 0)
  }), [insuranceLinks]);

  // ✅ Loading State
  if (loading && insuranceLinks.length === 0) {
    return (
      <div className="hmo-links-page d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border text-orange mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading HMO Links...</p>
        </div>
      </div>
    );
  }

  const header = renderHeader();

  return (
    <div className="hmo-links-page">
      <ToastContainer position="top-right" autoClose={3000} theme="light" />
      <ConfirmPopup />
      
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <Link to="/hmo-dashboard" className="brand-logo">
            <img src="images/fsl_logo.png" alt="Logo" style={{ height: '45px' }} />
            <span style={{ marginLeft: '8px', fontSize: '16px', fontWeight: '700', color: '#1f2937' }}>HMO Portal</span>
          </Link>
        </div>
        
        <nav className="sidebar-nav">
          {navItems.map((section, sectionIdx) => (
            <div key={sectionIdx} className="nav-section">
              <h6 className="nav-section-title">{section.section}</h6>
              <ul className="nav-list">
                {section.items.map((item, itemIdx) => (
                  <li key={itemIdx} className="nav-item">
                    {item.hasDropdown ? (
                      <>
                        <button
                          className={`nav-link ${item.active ? 'active' : ''} ${isExpanded(item.path) ? 'expanded' : ''}`}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleDropdown(item.path); }}
                          type="button"
                          aria-expanded={isExpanded(item.path)}
                        >
                          <i className={`fas ${item.icon} me-2`}></i>
                          <span>{item.name}</span>
                          <i className={`fas fa-chevron-down ms-auto dropdown-toggle ${isExpanded(item.path) ? 'rotated' : ''}`}></i>
                        </button>
                        <div className={`dropdown-menu ${isExpanded(item.path) ? 'show' : ''}`}>
                          <ul className="dropdown-list">
                            {item.children?.map((child, childIdx) => (
                              <li key={childIdx}>
                                <Link to={child.path} className={`dropdown-item ${child.active ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                                  <i className={`fas ${child.icon} me-2`}></i>
                                  <span>{child.name}</span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </>
                    ) : (
                      <Link to={item.path} className={`nav-link ${item.active ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                        <i className={`fas ${item.icon} me-2`}></i>
                        <span>{item.name}</span>
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        
        <div className="sidebar-footer">
          <div className="nav-section">
            <h6 className="nav-section-title">ACCOUNT</h6>
            <ul className="nav-list">
              <li><Link to="/hmo/profile" className="nav-link"><i className="fas fa-user me-2"></i>Profile</Link></li>
              <li><a href="/" className="nav-link" onClick={handleLogout}><i className="fas fa-sign-out-alt me-2"></i>Logout</a></li>
            </ul>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="main-content">
        <header className="top-header">
          <div className="header-left">
            <button className="sidebar-toggle d-lg-none" onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280' }}>
              <i className="fas fa-bars"></i>
            </button>
            <div className="page-title">
              <h4 className="mb-0" style={{ color: '#1f2937', fontWeight: '600' }}>HMO Links</h4>
              <small className="text-muted">Manage your insurance product links</small>
            </div>
          </div>
          <div className="header-right">
            <Button
              label="Generate Link"
              icon="fas fa-plus"
              onClick={handleOpenGenerateModal}
              style={{ backgroundColor: BRAND_ORANGE, borderColor: BRAND_ORANGE }}
              size="small"
            />
            <div className="user-avatar" style={{ backgroundColor: BRAND_ORANGE }} title="HMO Admin">
              H
            </div>
          </div>
        </header>

        <div className="content-wrapper" style={{ padding: '30px' }}>
          {/* TOP METRICS */}
          <div className="row mb-4" data-aos="fade-up">
            <div className="col-lg-3 col-md-6 mb-3">
              <Card className="stats-card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <p className="text-muted small mb-1">Total Links</p>
                      <h3 className="mb-0 fw-bold" style={{ color: '#1f2937' }}>{stats.total}</h3>
                    </div>
                    <Tag value="🔗" severity="info" />
                  </div>
                  <small className="text-muted">All time</small>
                </div>
              </Card>
            </div>
            <div className="col-lg-3 col-md-6 mb-3">
              <Card className="stats-card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <p className="text-muted small mb-1">Active Links</p>
                      <h3 className="mb-0 fw-bold" style={{ color: '#1f2937' }}>{stats.active}</h3>
                    </div>
                    <Tag value="✅" severity="success" />
                  </div>
                  <small className="text-muted">Currently live</small>
                </div>
              </Card>
            </div>
            <div className="col-lg-3 col-md-6 mb-3">
              <Card className="stats-card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <p className="text-muted small mb-1">Multiple Products</p>
                      <h3 className="mb-0 fw-bold" style={{ color: '#1f2937' }}>{stats.multiple}</h3>
                    </div>
                    <Tag value="📦" severity="warning" />
                  </div>
                  <small className="text-muted">Bundled links</small>
                </div>
              </Card>
            </div>
            <div className="col-lg-3 col-md-6 mb-3">
              <Card className="stats-card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <p className="text-muted small mb-1">Total Usage</p>
                      <h3 className="mb-0 fw-bold" style={{ color: '#1f2937' }}>{stats.totalUsage}</h3>
                    </div>
                    <Tag value="📊" severity="success" />
                  </div>
                  <small className="text-muted">Customer interactions</small>
                </div>
              </Card>
            </div>
          </div>

          {/* Advanced DataTable */}
          <Card className="border-0 shadow-sm p-5" data-aos="fade-up" data-aos-delay="100">
            <div className="card-body p-0">
              <DataTable
                value={insuranceLinks}
                paginator
                showGridlines
                rows={10}
                rowsPerPageOptions={[5, 10, 25, 50]}
                loading={loading}
                dataKey="id"
                filters={filters}
                globalFilterFields={['linkTitle', 'linkId', 'operations', 'status']}
                header={header}
                emptyMessage="No links found."
                onFilter={(e) => setFilters(e.filters)}
                tableStyle={{ minWidth: '1000px', marginRight: '1rem', marginTop: '1rem' }}
                className="p-datatable-sm"
                responsiveLayout="scroll"
              >
                <Column field="id" header="ID" sortable style={{ width: '80px' }} filter filterPlaceholder="Search" />
                <Column field="linkTitle" header="Link Title" sortable filter filterPlaceholder="Search title" style={{ minWidth: '12rem' }} />
                <Column field="linkId" header="Link ID" sortable filter filterPlaceholder="Search ID" style={{ width: '140px' }} body={(rowData) => (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <a 
                      href={generateLinkUrl(rowData.companyId, rowData.linkId)} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      style={{ color: BRAND_ORANGE, textDecoration: 'none', fontSize: '13px' }}
                      title="Preview link with agent attribution"
                    >
                      {rowData.linkId}
                    </a>
                    <button 
                      onClick={() => copyToClipboard(
                        generateLinkUrl(rowData.companyId, rowData.linkId), 
                        rowData.id
                      )} 
                      style={{ 
                        background: 'none', 
                        border: 'none', 
                        color: copiedId === rowData.id ? '#10b981' : '#6b7280', 
                        cursor: 'pointer', 
                        padding: '4px' 
                      }} 
                      title="Copy link with agent attribution"
                    >
                      <i className={`fas ${copiedId === rowData.id ? 'fa-check' : 'fa-copy'}`}></i>
                    </button>
                  </div>
                )} />
                <Column field="operations" header="Operations" sortable style={{ width: '150px' }} body={operationsBodyTemplate} filter filterField="operations" showFilterMatchModes={false} />
                <Column field="status" header="Status" sortable filterMenuStyle={{ width: '14rem' }} style={{ width: '120px' }} body={statusBodyTemplate} filter filterElement={statusFilterTemplate} />
                <Column field="dateGenerated" header="Date Generated" sortable style={{ width: '120px' }} />
                <Column header="Actions" body={actionBodyTemplate} style={{ width: '180px' }} exportable={false} />
              </DataTable>
            </div>
          </Card>
        </div>
      </div>

      {/* ✅ GENERATE LINK MODAL */}
      {showGenerateModal && (
        <div className="modal-overlay" onClick={handleCloseGenerateModal}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()} style={{ width: '700px', maxWidth: '95%' }}>
            <div className="modal-header" style={{ padding: '20px 30px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h5 className="mb-1" style={{ color: '#1f2937' }}><i className="fas fa-link me-2"></i>Generate New Link</h5>
                <p className="text-muted small mb-0">Select products to bundle with this link</p>
              </div>
              <button onClick={handleCloseGenerateModal} style={{ background: 'none', border: 'none', fontSize: '24px', color: '#6b7280', cursor: 'pointer', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }} onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'} onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}>×</button>
            </div>
            <form onSubmit={handleGenerateSubmit}>
              <div className="modal-content" style={{ padding: '25px 30px', overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
                {/* Link Title */}
                <div className="mb-4">
                  <label className="form-label fw-semibold small">Link Title <span className="text-danger">*</span></label>
                  <InputText value={generateForm.linkTitle} onChange={(e) => handleGenerateChange('linkTitle', e.target.value)} placeholder="e.g., Seniors Plans by YourHMO" disabled={isGenerating} className={generateErrors.linkTitle ? 'p-invalid' : ''} style={{ width: '100%' }} />
                  {generateErrors.linkTitle && <small className="p-error" style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{generateErrors.linkTitle}</small>}
                </div>

                {/* Operations */}
                <div className="mb-4">
                  <label className="form-label fw-semibold small">Operations <span className="text-danger">*</span></label>
                  <div className="d-flex flex-wrap gap-2">
                    {['Purchase', 'Renewal', 'ManagePolicy'].map(op => (
                      <label key={op} className="form-check-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', border: `1px solid ${generateForm.operations.includes(op) ? BRAND_ORANGE : '#d1d5db'}`, borderRadius: '8px', backgroundColor: generateForm.operations.includes(op) ? BRAND_ORANGE_LIGHT : 'white', cursor: isGenerating ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: generateForm.operations.includes(op) ? '600' : '500', color: generateForm.operations.includes(op) ? BRAND_ORANGE : '#374151' }}>
                        <input type="checkbox" className="form-check-input" checked={generateForm.operations.includes(op)} onChange={() => handleGenerateToggle('operations', op)} disabled={isGenerating} style={{ accentColor: BRAND_ORANGE }} />{op}
                      </label>
                    ))}
                  </div>
                  {generateErrors.operations && <small className="text-danger d-block mt-1">{generateErrors.operations}</small>}
                </div>

                {/* Products from API */}
                <div className="mb-4">
                  <label className="form-label fw-semibold small">Bundled Products <span className="text-danger">*</span></label>
                  {productsLoading ? (
                    <div className="text-center py-3 text-muted">
                      <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                      Loading products...
                    </div>
                  ) : (
                    <div className="d-flex flex-wrap gap-2" style={{ maxHeight: '200px', overflowY: 'auto', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#f9fafb' }}>
                      {products.map(product => (
                        <label key={product.id} className="form-check-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', border: `1px solid ${generateForm.productIds.includes(product.id) ? BRAND_ORANGE : '#d1d5db'}`, borderRadius: '6px', backgroundColor: generateForm.productIds.includes(product.id) ? BRAND_ORANGE_LIGHT : 'white', cursor: isGenerating ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: generateForm.productIds.includes(product.id) ? '600' : '500', color: generateForm.productIds.includes(product.id) ? BRAND_ORANGE : '#374151' }}>
                          <input type="checkbox" className="form-check-input" checked={generateForm.productIds.includes(product.id)} onChange={() => handleGenerateToggle('productIds', product.id)} disabled={isGenerating} style={{ accentColor: BRAND_ORANGE, width: '14px', height: '14px' }} />
                          {product.name}
                          <small className="text-muted ms-1">({product.category})</small>
                        </label>
                      ))}
                    </div>
                  )}
                  {generateErrors.productIds && <small className="text-danger d-block mt-1">{generateErrors.productIds}</small>}
                </div>

                {/* Charge Settings */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold small">Purchase Charge</label>
                    <Dropdown value={generateForm.purchaseCharge} onChange={(e) => handleGenerateChange('purchaseCharge', e.value)} options={[{ label: 'Customer', value: 'Customer' }, { label: 'Company', value: 'Company' }, { label: 'Shared', value: 'Shared' }]} optionLabel="label" optionValue="value" disabled={isGenerating} style={{ width: '100%' }} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold small">Renewal Charge</label>
                    <Dropdown value={generateForm.renewalCharge} onChange={(e) => handleGenerateChange('renewalCharge', e.value)} options={[{ label: 'Customer', value: 'Customer' }, { label: 'Company', value: 'Company' }, { label: 'Shared', value: 'Shared' }]} optionLabel="label" optionValue="value" disabled={isGenerating} style={{ width: '100%' }} />
                  </div>
                </div>

                {/* Charge Mode */}
                <div className="mb-4">
                  <label className="form-label fw-semibold small">Charge Mode</label>
                  <div className="btn-group" role="group">
                    {['Disabled', 'Enabled'].map(mode => (
                      <button key={mode} type="button" className={`btn ${generateForm.chargeMode === mode ? 'btn-active' : 'btn-outline-secondary'}`} onClick={() => handleGenerateChange('chargeMode', mode)} disabled={isGenerating}
                        style={{ backgroundColor: generateForm.chargeMode === mode ? BRAND_ORANGE : 'white', color: generateForm.chargeMode === mode ? 'white' : '#6b7280', border: '1px solid #d1d5db', borderLeft: mode !== 'Disabled' ? 'none' : undefined, padding: '8px 20px', fontWeight: '500', borderRadius: mode === 'Disabled' ? '6px 0 0 6px' : mode === 'Enabled' ? '0 6px 6px 0' : undefined, opacity: isGenerating ? 0.7 : 1, cursor: isGenerating ? 'not-allowed' : 'pointer' }}>{mode}</button>
                    ))}
                  </div>
                </div>

                {/* Expiry */}
                <div className="mb-4">
                  <label className="form-label fw-semibold small">Expiry Date</label>
                  <div className="d-flex align-items-center gap-3">
                    <label className="form-check-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: isGenerating ? 'not-allowed' : 'pointer' }}>
                      <input type="radio" name="expiryType" checked={generateForm.expiryType === 'indefinite'} onChange={() => handleGenerateChange('expiryType', 'indefinite')} disabled={isGenerating} style={{ accentColor: BRAND_ORANGE }} /><span style={{ fontSize: '14px', fontWeight: '500' }}>Indefinite</span>
                    </label>
                    <label className="form-check-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: isGenerating ? 'not-allowed' : 'pointer' }}>
                      <input type="radio" name="expiryType" checked={generateForm.expiryType === 'custom'} onChange={() => handleGenerateChange('expiryType', 'custom')} disabled={isGenerating} style={{ accentColor: BRAND_ORANGE }} /><span style={{ fontSize: '14px', fontWeight: '500' }}>Custom</span>
                    </label>
                    {generateForm.expiryType === 'custom' && <InputText type="date" value={generateForm.customExpiry} onChange={(e) => handleGenerateChange('customExpiry', e.target.value)} disabled={isGenerating} min={new Date().toISOString().split('T')[0]} style={{ width: 'auto', minWidth: '180px' }} />}
                  </div>
                  {generateErrors.customExpiry && <small className="text-danger d-block mt-1">{generateErrors.customExpiry}</small>}
                </div>
              </div>
              <div className="modal-footer" style={{ padding: '15px 30px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" className="btn btn-secondary" onClick={handleCloseGenerateModal} disabled={isGenerating}>Cancel</button>
                <button type="submit" className="btn" disabled={isGenerating} style={{ backgroundColor: BRAND_ORANGE, color: 'white', border: 'none', padding: '10px 24px', fontWeight: '600', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {isGenerating ? <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>Creating...</> : <><i className="fas fa-link"></i>Generate Link</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ✅ EDIT MODAL */}
      {showEditModal && (
        <div className="modal-overlay" onClick={handleCloseEditModal}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()} style={{ width: '700px', maxWidth: '95%' }}>
            <div className="modal-header" style={{ padding: '20px 30px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div><h5 className="mb-1" style={{ color: '#1f2937' }}><i className="fas fa-edit me-2"></i>Edit Link</h5></div>
              <button onClick={handleCloseEditModal} style={{ background: 'none', border: 'none', fontSize: '24px', color: '#6b7280', cursor: 'pointer', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }}>×</button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="modal-content" style={{ padding: '25px 30px', overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
                <div className="mb-4">
                  <label className="form-label fw-semibold small">Link Title <span className="text-danger">*</span></label>
                  <InputText value={editForm.linkTitle || ''} onChange={(e) => handleEditChange('linkTitle', e.target.value)} disabled={isEditing} className={editErrors.linkTitle ? 'p-invalid' : ''} style={{ width: '100%' }} />
                  {editErrors.linkTitle && <small className="p-error" style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{editErrors.linkTitle}</small>}
                </div>
                
                {/* Operations */}
                <div className="mb-4">
                  <label className="form-label fw-semibold small">Operations <span className="text-danger">*</span></label>
                  <div className="d-flex flex-wrap gap-2">
                    {['Purchase', 'Renewal', 'ManagePolicy'].map(op => (
                      <label key={op} className="form-check-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', border: `1px solid ${editForm.operations?.includes(op) ? BRAND_ORANGE : '#d1d5db'}`, borderRadius: '8px', backgroundColor: editForm.operations?.includes(op) ? BRAND_ORANGE_LIGHT : 'white', cursor: isEditing ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: editForm.operations?.includes(op) ? '600' : '500', color: editForm.operations?.includes(op) ? BRAND_ORANGE : '#374151' }}>
                        <input type="checkbox" className="form-check-input" checked={editForm.operations?.includes(op)} onChange={() => handleEditToggle('operations', op)} disabled={isEditing} style={{ accentColor: BRAND_ORANGE }} />{op}
                      </label>
                    ))}
                  </div>
                  {editErrors.operations && <small className="text-danger d-block mt-1">{editErrors.operations}</small>}
                </div>

                {/* Products */}
                <div className="mb-4">
                  <label className="form-label fw-semibold small">Bundled Products <span className="text-danger">*</span></label>
                  {productsLoading ? (
                    <div className="text-center py-3 text-muted">
                      <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                      Loading products...
                    </div>
                  ) : (
                    <div className="d-flex flex-wrap gap-2" style={{ maxHeight: '200px', overflowY: 'auto', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#f9fafb' }}>
                      {products.map(product => (
                        <label key={product.id} className="form-check-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', border: `1px solid ${editForm.productIds?.includes(product.id) ? BRAND_ORANGE : '#d1d5db'}`, borderRadius: '6px', backgroundColor: editForm.productIds?.includes(product.id) ? BRAND_ORANGE_LIGHT : 'white', cursor: isEditing ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: editForm.productIds?.includes(product.id) ? '600' : '500', color: editForm.productIds?.includes(product.id) ? BRAND_ORANGE : '#374151' }}>
                          <input type="checkbox" className="form-check-input" checked={editForm.productIds?.includes(product.id)} onChange={() => handleEditToggle('productIds', product.id)} disabled={isEditing} style={{ accentColor: BRAND_ORANGE, width: '14px', height: '14px' }} />
                          {product.name}
                          <small className="text-muted ms-1">({product.category})</small>
                        </label>
                      ))}
                    </div>
                  )}
                  {editErrors.productIds && <small className="text-danger d-block mt-1">{editErrors.productIds}</small>}
                </div>

                {/* Charge Settings */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold small">Purchase Charge</label>
                    <Dropdown value={editForm.purchaseCharge} onChange={(e) => handleEditChange('purchaseCharge', e.value)} options={[{ label: 'Customer', value: 'Customer' }, { label: 'Company', value: 'Company' }, { label: 'Shared', value: 'Shared' }]} optionLabel="label" optionValue="value" disabled={isEditing} style={{ width: '100%' }} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold small">Renewal Charge</label>
                    <Dropdown value={editForm.renewalCharge} onChange={(e) => handleEditChange('renewalCharge', e.value)} options={[{ label: 'Customer', value: 'Customer' }, { label: 'Company', value: 'Company' }, { label: 'Shared', value: 'Shared' }]} optionLabel="label" optionValue="value" disabled={isEditing} style={{ width: '100%' }} />
                  </div>
                </div>

                {/* Charge Mode */}
                <div className="mb-4">
                  <label className="form-label fw-semibold small">Charge Mode</label>
                  <div className="btn-group" role="group">
                    {['Disabled', 'Enabled'].map(mode => (
                      <button key={mode} type="button" className={`btn ${editForm.chargeMode === mode ? 'btn-active' : 'btn-outline-secondary'}`} onClick={() => handleEditChange('chargeMode', mode)} disabled={isEditing}
                        style={{ backgroundColor: editForm.chargeMode === mode ? BRAND_ORANGE : 'white', color: editForm.chargeMode === mode ? 'white' : '#6b7280', border: '1px solid #d1d5db', borderLeft: mode !== 'Disabled' ? 'none' : undefined, padding: '8px 20px', fontWeight: '500', borderRadius: mode === 'Disabled' ? '6px 0 0 6px' : mode === 'Enabled' ? '0 6px 6px 0' : undefined, opacity: isEditing ? 0.7 : 1, cursor: isEditing ? 'not-allowed' : 'pointer' }}>{mode}</button>
                    ))}
                  </div>
                </div>

                {/* Expiry */}
                <div className="mb-4">
                  <label className="form-label fw-semibold small">Expiry Date</label>
                  <div className="d-flex align-items-center gap-3">
                    <label className="form-check-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: isEditing ? 'not-allowed' : 'pointer' }}>
                      <input type="radio" name="expiryType" checked={editForm.expiryType === 'indefinite'} onChange={() => handleEditChange('expiryType', 'indefinite')} disabled={isEditing} style={{ accentColor: BRAND_ORANGE }} /><span style={{ fontSize: '14px', fontWeight: '500' }}>Indefinite</span>
                    </label>
                    <label className="form-check-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: isEditing ? 'not-allowed' : 'pointer' }}>
                      <input type="radio" name="expiryType" checked={editForm.expiryType === 'custom'} onChange={() => handleEditChange('expiryType', 'custom')} disabled={isEditing} style={{ accentColor: BRAND_ORANGE }} /><span style={{ fontSize: '14px', fontWeight: '500' }}>Custom</span>
                    </label>
                    {editForm.expiryType === 'custom' && <InputText type="date" value={editForm.customExpiry} onChange={(e) => handleEditChange('customExpiry', e.target.value)} disabled={isEditing} min={new Date().toISOString().split('T')[0]} style={{ width: 'auto', minWidth: '180px' }} />}
                  </div>
                  {editErrors.customExpiry && <small className="text-danger d-block mt-1">{editErrors.customExpiry}</small>}
                </div>
              </div>
              <div className="modal-footer" style={{ padding: '15px 30px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" className="btn btn-secondary" onClick={handleCloseEditModal} disabled={isEditing}>Cancel</button>
                <button type="submit" className="btn" disabled={isEditing} style={{ backgroundColor: BRAND_ORANGE, color: 'white', border: 'none', padding: '10px 24px', fontWeight: '600', borderRadius: '8px' }}>
                  {isEditing ? <><span className="spinner-border spinner-border-sm"></span>Saving...</> : <><i className="fas fa-save"></i>Save Changes</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ✅ ENHANCED DETAILS MODAL with Tabs */}
      {showDetailsModal && selectedLink && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="modal-header" style={{ padding: '20px 30px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h5 className="mb-1" style={{ color: '#1f2937' }}>Link details</h5>
                <p className="text-muted small mb-0">{selectedLink.linkTitle}</p>
              </div>
              <button onClick={handleCloseModal} style={{ background: 'none', border: 'none', fontSize: '24px', color: '#6b7280', cursor: 'pointer', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', transition: 'all 0.2s ease' }} onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'} onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}>×</button>
            </div>

            {/* Modal Tabs */}
            <div className="modal-tabs" style={{ padding: '0 30px', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: '30px' }}>
              <button onClick={() => setActiveDetailsTab('details')} style={{ padding: '15px 0', background: 'none', border: 'none', borderBottom: activeDetailsTab === 'details' ? '2px solid ' + BRAND_ORANGE : '2px solid transparent', color: activeDetailsTab === 'details' ? BRAND_ORANGE : '#6b7280', fontWeight: '600', cursor: 'pointer', fontSize: '14px' }}>Link details</button>
              <button onClick={() => setActiveDetailsTab('history')} style={{ padding: '15px 0', background: 'none', border: 'none', borderBottom: activeDetailsTab === 'history' ? '2px solid ' + BRAND_ORANGE : '2px solid transparent', color: activeDetailsTab === 'history' ? BRAND_ORANGE : '#6b7280', fontWeight: '600', cursor: 'pointer', fontSize: '14px' }}>Usage history</button>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
                <button onClick={() => handleOpenEditModal(selectedLink)} style={{ padding: '8px 16px', background: 'none', border: 'none', color: '#10b981', cursor: 'pointer', fontWeight: '500', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}><i className="fas fa-edit"></i> Edit link</button>
                <button onClick={(e) => handleDeleteLink(e, selectedLink.id, selectedLink.linkTitle)} style={{ padding: '8px 16px', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: '500', fontSize: '13px' }}>Delete link</button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="modal-content" style={{ padding: '30px', overflowY: 'auto', maxHeight: 'calc(100vh - 250px)' }}>
              {activeDetailsTab === 'details' ? (
                <div>
                  {/* Info Grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '30px', marginBottom: '30px' }}>
                    <div><div className="text-muted small mb-1">Link type</div><div className="fw-semibold" style={{ fontSize: '14px' }}>{selectedLink.linkType || 'N/A'}</div></div>
                    <div><div className="text-muted small mb-1">Link title</div><div className="fw-semibold" style={{ fontSize: '14px' }}>{selectedLink.linkTitle}</div></div>
                    <div><div className="text-muted small mb-1">Status</div><Tag value={selectedLink.linkStatus || selectedLink.status} severity={selectedLink.linkStatus === 'Active' ? 'success' : 'danger'} className="fw-semibold" /></div>
                    <div><div className="text-muted small mb-1">Purchase charge</div><div className="fw-semibold" style={{ fontSize: '14px' }}>{selectedLink.purchaseCharge || 'N/A'}</div></div>
                    <div><div className="text-muted small mb-1">Renewal charge</div><div className="fw-semibold" style={{ fontSize: '14px' }}>{selectedLink.renewalCharge || 'N/A'}</div></div>
                    <div><div className="text-muted small mb-1">Date generated</div><div className="fw-semibold" style={{ fontSize: '14px' }}>{selectedLink.dateGenerated}</div></div>
                    <div><div className="text-muted small mb-1">Activation date</div><div className="fw-semibold" style={{ fontSize: '14px' }}>{selectedLink.activationDate || 'N/A'}</div></div>
                    <div><div className="text-muted small mb-1">Expiry date</div><div className="fw-semibold" style={{ fontSize: '14px' }}>{selectedLink.expiryDate || 'Indefinite'}</div></div>
                    <div><div className="text-muted small mb-1">Link URL</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <a 
                          href={generateLinkUrl(selectedLink.companyId, selectedLink.linkId)} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          style={{ color: BRAND_ORANGE, textDecoration: 'none', fontSize: '13px', wordBreak: 'break-all' }}
                        >
                          {selectedLink.linkId}
                        </a>
                        <button 
                          onClick={() => copyToClipboard(
                            generateLinkUrl(selectedLink.companyId, selectedLink.linkId), 
                            selectedLink.id
                          )}
                          style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer' }}
                          title="Copy link with agent attribution"
                        >
                          <i className="fas fa-copy"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Status Toggle */}
                  <div style={{ marginBottom: '30px' }}>
                    <div className="text-muted small mb-2">Link status</div>
                    <div className="btn-group" role="group">
                      <button className="btn" onClick={() => handleToggleStatus(selectedLink.id, selectedLink.status)} style={{ backgroundColor: selectedLink.status === 'Active' ? '#10b981' : '#f3f4f6', color: selectedLink.status === 'Active' ? 'white' : '#6b7280', border: 'none', padding: '8px 20px', fontWeight: '600', borderRadius: '6px 0 0 6px', cursor: 'pointer' }}>Enabled</button>
                      <button className="btn" onClick={() => handleToggleStatus(selectedLink.id, selectedLink.status)} style={{ backgroundColor: selectedLink.status !== 'Active' ? '#6b7280' : '#f3f4f6', color: selectedLink.status !== 'Active' ? 'white' : '#6b7280', border: 'none', padding: '8px 20px', fontWeight: '600', borderRadius: '0 6px 6px 0', cursor: 'pointer' }}>Disable</button>
                    </div>
                  </div>

                  {/* Operations */}
                  <div style={{ marginBottom: '30px' }}>
                    <h6 className="mb-3" style={{ color: '#1f2937' }}>Operations</h6>
                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                      {selectedLink.operations?.split(',').map((op, idx) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', backgroundColor: '#f3f4f6', borderRadius: '8px' }}><i className="fas fa-check-circle" style={{ color: '#10b981' }}></i><span style={{ fontWeight: '500', fontSize: '14px' }}>{op.trim()}</span></div>
                      ))}
                    </div>
                  </div>

                  {/* Bundled Products */}
                  <div>
                    <h6 className="mb-3" style={{ color: '#1f2937' }}>Bundled products ({selectedLink.bundledProducts?.length || 0})</h6>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {(selectedLink.bundledProducts || []).map((product, idx) => {
                        const productName = product.name || product.Name || 'Unknown Product';
                        const productPremium = product.minimumPremium ?? product.MinimumPremium ?? 0;
                        const productCategory = product.categoryName || product.CategoryName || '';
                        
                        return (
                          <span
                            key={product.id || product.Id || idx}
                            style={{
                              display: 'inline-block',
                              padding: '8px 16px',
                              backgroundColor: '#f3f4f6',
                              borderRadius: '8px',
                              fontSize: '13px',
                              color: '#374151',
                              fontWeight: '500',
                              width: 'fit-content'
                            }}
                          >
                            {productName}
                            {productCategory && <span className="text-muted ms-1">({productCategory})</span>}
                            {productPremium > 0 && <span className="ms-2">- ₦{Number(productPremium).toLocaleString()}</span>}
                          </span>
                        );
                      })}
                      {(!selectedLink.bundledProducts || selectedLink.bundledProducts.length === 0) && (
                        <p className="text-muted small">No products bundled with this link.</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  {/* Usage History Table Header */}
                  <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr 1fr', gap: '20px', padding: '12px 20px', backgroundColor: '#f9fafb', borderRadius: '8px 8px 0 0', border: '1px solid #e5e7eb', borderBottom: 'none', fontWeight: '600', fontSize: '13px', color: '#374151' }}>
                    <div>S/N</div><div>Customer Name</div><div>Actions</div><div>Product/Policy Name</div>
                  </div>
                  {/* Usage History Table Body */}
                  <div style={{ border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 8px 8px' }}>
                    <div style={{ padding: '60px 20px', textAlign: 'center' }}>
                      <div style={{
                        width: '64px',
                        height: '64px',
                        backgroundColor: '#d1fae5',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 20px'
                      }}>
                        <i className="fas fa-search" style={{ color: '#10b981', fontSize: '24px' }}></i>
                      </div>
                      <h6 style={{ color: '#1f2937', marginBottom: '8px', fontWeight: '600' }}>No usage history</h6>
                      <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
                        This link has not been used yet. Share it with customers to start tracking usage.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CSS Styles */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideIn { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
        
        /* Scrollbar */
        ::-webkit-scrollbar { width: 10px; height: 10px; }
        ::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
        ::-webkit-scrollbar-thumb { background: ${BRAND_ORANGE}; border-radius: 10px; border: 2px solid #f1f1f1; }
        ::-webkit-scrollbar-thumb:hover { background: ${BRAND_ORANGE_DARK}; }
        * { scrollbar-width: thin; scrollbar-color: ${BRAND_ORANGE} #f1f1f1; }
        
        .hmo-links-page { display: flex; min-height: 100vh; background: #f8f9fa; }
        
        /* Sidebar */
        .sidebar {
          width: 260px; background: white; color: #333; position: fixed; left: 0; top: 0;
          height: 100vh; overflow-y: auto; transition: transform 0.3s ease; z-index: 1000;
          border-right: 1px solid #e5e7eb; border-left: 3px solid ${BRAND_ORANGE};
        }
        .sidebar-header { padding: 20px; border-bottom: 1px solid #e5e7eb; display: flex; align-items: center; justify-content: center; }
        .brand-logo { color: #333; font-size: 20px; font-weight: 700; text-decoration: none; display: flex; align-items: center; gap: 8px; }
        .sidebar-nav { padding: 20px 0; }
        .nav-section { margin-bottom: 25px; }
        .nav-section-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: ${BRAND_ORANGE}; padding: 0 20px; margin-bottom: 10px; }
        .nav-list { list-style: none; padding: 0; margin: 0; }
        .nav-item { margin-bottom: 2px; }
        
        /* Nav Link */
        .nav-link {
          display: flex; align-items: center; padding: 12px 20px; color: #374151; text-decoration: none;
          transition: all 0.3s ease; font-weight: 500; font-size: 14px; border-left: 3px solid transparent;
          background: none; border: none; width: 100%; text-align: left; cursor: pointer;
        }
        .nav-link i { width: 20px; margin-right: 12px; font-size: 16px; color: #6b7280; }
        .nav-link:hover { background-color: ${BRAND_ORANGE_LIGHT}; color: ${BRAND_ORANGE}; border-left-color: ${BRAND_ORANGE}; }
        .nav-link:hover i { color: ${BRAND_ORANGE}; }
        .nav-link.active { background-color: ${BRAND_ORANGE}; color: white; border-left-color: ${BRAND_ORANGE}; }
        .nav-link.active i { color: white; }
        
        /* Dropdown */
        .nav-link.has-dropdown, .nav-link.expanded { position: relative; padding-right: 35px; }
        .nav-link .dropdown-toggle {
          position: absolute; right: 15px; top: 50%; transform: translateY(-50%);
          font-size: 10px; color: #6b7280; transition: transform 0.3s ease; pointer-events: none;
        }
        .nav-link .dropdown-toggle.rotated { transform: translateY(-50%) rotate(180deg); }
        .nav-link.expanded .dropdown-toggle { color: ${BRAND_ORANGE}; }
        
        .dropdown-menu {
          max-height: 0; overflow: hidden; transition: max-height 0.4s ease, opacity 0.4s ease;
          opacity: 0; background: #f8f9fa; border-radius: 0 0 8px 8px; margin: 0; padding: 0;
        }
        .dropdown-menu.show { max-height: 600px; opacity: 1; box-shadow: inset 0 2px 4px rgba(0,0,0,0.05); }
        .dropdown-list { list-style: none; padding: 0; margin: 0; }
        .dropdown-item {
          display: flex; align-items: center; padding: 10px 20px 10px 52px; color: #4b5563;
          text-decoration: none; font-size: 13px; transition: all 0.2s ease; border-left: 3px solid transparent;
        }
        .dropdown-item:hover { background-color: ${BRAND_ORANGE_LIGHT}; color: ${BRAND_ORANGE}; border-left-color: ${BRAND_ORANGE}; }
        .dropdown-item.active { background-color: ${BRAND_ORANGE}; color: white; border-left-color: ${BRAND_ORANGE}; }
        .dropdown-item .dropdown-icon { width: 16px; margin-right: 10px; font-size: 14px; color: #6b7280; }
        .dropdown-item:hover .dropdown-icon, .dropdown-item.active .dropdown-icon { color: inherit; }
        
        .sidebar-footer { margin-top: auto; padding: 20px; border-top: 1px solid #e5e7eb; }
        .sidebar-footer .nav-link { color: #ef4444; padding: 10px 20px; background: none; border: none; width: 100%; text-align: left; cursor: pointer; }
        .sidebar-footer .nav-link:hover { background-color: #fef2f2; }
        
        /* Main Content */
        .main-content { flex: 1; margin-left: 260px; display: flex; flex-direction: column; background: #f9fafb; }
        .top-header { background: white; padding: 15px 30px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); display: flex; justify-content: space-between; align-items: center; position: sticky; top: 0; z-index: 100; }
        .header-left { display: flex; align-items: center; gap: 20px; }
        .sidebar-toggle { background: none; border: none; font-size: 20px; color: #374151; cursor: pointer; display: none; }
        .page-title { display: flex; flex-direction: column; color: #1f2937; }
        .header-right { display: flex; align-items: center; gap: 15px; }
        .user-avatar { width: 40px; height: 40px; border-radius: 50%; color: white; display: flex; align-items: center; justify-content: center; font-weight: 600; cursor: pointer; font-size: 14px; }
        
        /* Content */
        .content-wrapper { padding: 30px; flex: 1; }
        .card { border-radius: 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); background: white; border: none; }
        .card-header { border-bottom: 1px solid #f1f5f9 !important; }
        .stats-card { transition: transform 0.2s ease; }
        .stats-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.08) !important; }
        
        /* PrimeReact */
        .p-card { border-radius: 12px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important; }
        .p-progressbar { border-radius: 4px !important; overflow: hidden; }
        .p-progressbar .p-progressbar-value { transition: width 0.3s ease; }
        .p-progressbar.p-progressbar-custom-orange .p-progressbar-value { background: ${BRAND_ORANGE} !important; }
        .p-progressbar.p-progressbar-custom-green .p-progressbar-value { background: ${CHART_COLORS.success} !important; }
        .p-progressbar.p-progressbar-custom-purple .p-progressbar-value { background: ${CHART_COLORS.secondary} !important; }
        .p-progressbar.p-progressbar-custom-blue .p-progressbar-value { background: ${CHART_COLORS.info} !important; }
        .p-progressbar.p-progressbar-custom-warning .p-progressbar-value { background: ${CHART_COLORS.warning} !important; }
        .p-button { border-radius: 8px !important; font-weight: 500; }
        .p-button.p-button-secondary { background: #f3f4f6 !important; color: #374151 !important; border: 1px solid #d1d5db !important; }
        .p-button.p-button-secondary:hover { background: #e5e7eb !important; }
        .p-button.p-button-info { background: ${CHART_COLORS.info} !important; border-color: ${CHART_COLORS.info} !important; }
        .p-button.p-button-warning { background: ${CHART_COLORS.warning} !important; border-color: ${CHART_COLORS.warning} !important; }
        
        /* DataTable */
        .p-datatable .p-datatable-header { background: #f8f9fa !important; border-bottom: 1px solid #e5e7eb !important; padding: 1rem !important; }
        .p-datatable .p-datatable-thead > tr > th { background: #f8f9fa !important; color: #374151 !important; font-weight: 600 !important; font-size: 13px !important; text-transform: uppercase !important; letter-spacing: 0.5px !important; }
        .p-datatable .p-datatable-tbody > tr > td { padding: 0.75rem 1rem !important; font-size: 14px !important; }
        .p-datatable .p-paginator { padding: 1rem !important; background: #f8f9fa !important; }
        
        /* Modal */
        .modal-overlay { position: fixed; top: 0; right: 0; width: 100%; height: 100%; background-color: rgba(0, 0, 0, 0.5); z-index: 2000; display: flex; justify-content: flex-end; animation: fadeIn 0.3s ease; }
        .modal-panel { width: 900px; max-width: 90%; background: white; height: 100vh; overflow-y: auto; animation: slideInRight 0.3s ease; box-shadow: -4px 0 12px rgba(0,0,0,0.15); }
        
        /* Form */
        .form-label { display: block; margin-bottom: 6px; font-weight: 600; color: #374151; font-size: 14px; }
        .p-inputtext, .p-dropdown { border-radius: 8px !important; border: 1px solid #d1d5db !important; padding: 10px 14px !important; }
        .p-inputtext:focus, .p-dropdown:focus { border-color: ${BRAND_ORANGE} !important; box-shadow: 0 0 0 2px rgba(248,148,35,0.2) !important; }
        .p-invalid { border-color: #ef4444 !important; }
        .p-error { color: #ef4444 !important; font-size: 12px; }
        
        /* Responsive */
        @media (max-width: 992px) {
          .sidebar { transform: translateX(-100%); }
          .sidebar.sidebar-open { transform: translateX(0); }
          .main-content { margin-left: 0; }
          .sidebar-toggle { display: block; }
        }
        @media (max-width: 768px) {
          .content-wrapper { padding: 15px; }
          .top-header { padding: 15px; }
          .header-right .btn span { display: none; }
          .modal-panel { width: 100% !important; }
        }
      `}</style>
    </div>
  );
}

export default HmoLinks;